import React from "react";
import { Link } from "react-router-dom";
import MarketingLayout from "./MarketingLayout";
import { POSTS } from "@/lib/blogPosts";
import { useSeo } from "@/lib/seo";

const fmtDate = (iso) => {
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

export default function Blog() {
  useSeo({
    title: "Blog – FamFeel Family Check-In",
    description:
      "Short essays on family life, ambient awareness, and the small tools that keep households close — from the team behind FamFeel.",
    path: "/blog",
    jsonLdId: "ld-blog",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Blog",
          "name": "The FamFeel Blog",
          "url": typeof window !== "undefined" ? window.location.origin + "/blog" : undefined,
          "description":
            "Essays on family life, ambient awareness, and the small tools that keep households close.",
          "blogPost": POSTS.map((p) => ({
            "@type": "BlogPosting",
            "headline": p.title,
            "datePublished": p.date,
            "url": typeof window !== "undefined" ? `${window.location.origin}/blog/${p.slug}` : undefined,
          })),
        },
        {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": typeof window !== "undefined" ? window.location.origin + "/" : undefined },
            { "@type": "ListItem", "position": 2, "name": "Blog", "item": typeof window !== "undefined" ? window.location.origin + "/blog" : undefined },
          ],
        },
      ],
    },
  });

  return (
    <MarketingLayout>
      <section className="px-6 md:px-10 pt-10 md:pt-16 pb-10 max-w-4xl">
        <div className="label-tiny mb-4">Blog</div>
        <h1
          className="font-display-italic text-6xl md:text-8xl leading-[0.98] tracking-tight mb-4"
          data-testid="blog-h1"
        >
          Small things,
          <br />
          written down.
        </h1>
        <p className="text-[17px] md:text-xl text-[color:var(--ink-2)] leading-snug max-w-2xl mb-14">
          Essays on family life, the stuff that happens in between, and the
          quiet tools that keep households close.
        </p>

        <div className="divide-y divide-[color:var(--line-soft)]" data-testid="blog-list">
          {POSTS.map((p) => (
            <article key={p.slug} className="py-10">
              <Link
                to={`/blog/${p.slug}`}
                className="group flex gap-5 md:gap-8 items-start hover:opacity-95 transition-opacity"
                data-testid={`blog-card-${p.slug}`}
              >
                <div
                  className="w-16 h-16 md:w-20 md:h-20 rounded-xl flex items-center justify-center text-3xl md:text-4xl flex-shrink-0"
                  style={{ background: p.tint }}
                  aria-hidden="true"
                >
                  {p.heroEmoji}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="label-tiny mb-2 text-[color:var(--ink-3)]">
                    {fmtDate(p.date)} &nbsp;·&nbsp; {p.readMinutes} min read
                  </div>
                  <h2 className="font-display text-2xl md:text-3xl leading-tight tracking-tight mb-2 group-hover:underline decoration-1 underline-offset-4">
                    {p.title}
                  </h2>
                  <p className="text-[16px] text-[color:var(--ink-2)] leading-snug max-w-2xl">
                    {p.excerpt}
                  </p>
                </div>
              </Link>
            </article>
          ))}
        </div>
      </section>
    </MarketingLayout>
  );
}
